/**
 * @fileoverview URL-driven breadcrumb links. Injects a trail from pathname into [vdx-breadcrumbs].
 */

import { logWarn } from './errors.js';

const BREADCRUMB_SELECTOR = '[vdx-breadcrumbs]';
const ARIA_LABEL = 'Breadcrumb';
/** Default separator when vdx-breadcrumbs has no value. Common options: ">", "-", "/", "·", "›", "»", "|", "→" */
const DEFAULT_SEPARATOR = '>';
const HOME_LABEL = 'Home';

/**
 * Extracts non-empty path segments from the current pathname.
 * @returns {string[]}
 */
function _parsePathSegments() {
    if (typeof window === 'undefined' || !window.location || !window.location.pathname) {
        return [];
    }
    const pathname = window.location.pathname;
    return pathname.split('/').filter(Boolean);
}

/**
 * Converts a slug to title case (hyphens to spaces, capitalize first letter of each word).
 * @param {string} segment
 * @returns {string}
 */
function _generateLabel(segment) {
    if (!segment || typeof segment !== 'string') return '';
    return segment
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Builds trail items for path segments only (no Home): [{ href, label, isCurrent }].
 * @param {string[]} segments
 * @returns {{ href: string, label: string, isCurrent: boolean }[]}
 */
function _buildTrail(segments) {
    const trail = [];
    let path = '';
    for (let i = 0; i < segments.length; i++) {
        path += '/' + segments[i];
        const isCurrent = i === segments.length - 1;
        trail.push({
            href: path + (isCurrent ? '' : '/'),
            label: _generateLabel(segments[i]),
            isCurrent
        });
    }
    return trail;
}

/**
 * Renders the breadcrumb trail into the container. If existingHome is provided (e.g. an existing Home link), it is used as the first item.
 * @param {Element} container
 * @param {{ href: string, label: string, isCurrent: boolean }[]} segmentItems
 * @param {Element | null} [existingHome] - Optional existing element to use as the first (Home) breadcrumb.
 * @param {string} [linkClass] - Optional class to apply to injected <a> tags (e.g. from existing Home link).
 * @param {string} [separator] - Character(s) between items (e.g. ">", "-", "/", "·", "›", "»", "|", "→"). Default ">".
 */
function _renderTrail(container, segmentItems, existingHome, linkClass, separator) {
    const sep = (separator != null && String(separator) !== '') ? String(separator) : DEFAULT_SEPARATOR;
    const ol = document.createElement('ol');
    ol.setAttribute('aria-label', ARIA_LABEL);

    const hasSegments = segmentItems.length > 0;

    const firstLi = document.createElement('li');
    if (existingHome) {
        firstLi.appendChild(existingHome);
    } else {
        const a = document.createElement('a');
        a.href = '/';
        a.textContent = HOME_LABEL;
        if (linkClass) a.className = linkClass;
        firstLi.appendChild(a);
    }
    if (hasSegments) {
        const sepSpan = document.createElement('span');
        sepSpan.setAttribute('aria-hidden', 'true');
        sepSpan.textContent = ' ' + sep + ' ';
        firstLi.appendChild(sepSpan);
    }
    ol.appendChild(firstLi);

    segmentItems.forEach((item, index) => {
        const li = document.createElement('li');
        if (item.isCurrent) {
            li.textContent = item.label;
            li.setAttribute('aria-current', 'page');
        } else {
            const a = document.createElement('a');
            a.href = item.href;
            a.textContent = item.label;
            if (linkClass) a.className = linkClass;
            li.appendChild(a);
        }
        if (index < segmentItems.length - 1) {
            const sepSpan = document.createElement('span');
            sepSpan.setAttribute('aria-hidden', 'true');
            sepSpan.textContent = ' ' + sep + ' ';
            li.appendChild(sepSpan);
        }
        ol.appendChild(li);
    });

    container.setAttribute('aria-label', ARIA_LABEL);
    container.innerHTML = '';
    container.appendChild(ol);
}

/**
 * Initializes breadcrumbs for one container: builds trail from pathname and renders.
 * @param {Element} container - Element with [vdx-breadcrumbs].
 */
function _initContainer(container) {
    const existingHome = container.querySelector('a[href="/"]') || container.firstElementChild || null;
    const linkClass = existingHome && existingHome.className ? String(existingHome.className).trim() : '';
    const separator = container.getAttribute('vdx-breadcrumbs');
    const segments = _parsePathSegments();
    const segmentItems = _buildTrail(segments);
    _renderTrail(container, segmentItems, existingHome, linkClass, separator);
}

/**
 * Initializes breadcrumbs: finds all [vdx-breadcrumbs], builds trail from pathname, and renders each.
 * No-op if no containers. Logs a warning if none found.
 */
export function initBreadcrumbs() {
    const containers = document.querySelectorAll(BREADCRUMB_SELECTOR);
    if (!containers.length) {
        logWarn('No element with [vdx-breadcrumbs] found.', { module: 'breadcrumbs' });
        return;
    }
    containers.forEach(_initContainer);
}
